import React, { useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next'; // Import useTranslation
import cn from 'classnames';
import style from "../common.module.scss"
import LineIcon from '../../../shared/icons/LineIcon';
import { getCountryIcon } from '../../../../utils/getCountryIcon';

const OrdersInfoDirections = ({ item, senderName = '', recipientName = '', isMarketplacePage = false }) => {
    const { t } = useTranslation();

    const copyAddress = async (text) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t("orderInfo.ordersInfoAddressList.addressCopied"));
        } catch {
            toast.error(t("orderInfo.ordersInfoAddressList.copyFailed"));
        }
    };

    const getAddressCopyProps = (country, city, address) => {
        const text = [country, city, address].filter(Boolean).join(", ");
        const label = t("orderInfo.ordersInfoAddressList.copyAddress");
        return {
            className: cn(style.shipping_column, style.shipping_column_copy),
            role: "button",
            tabIndex: text ? 0 : -1,
            "aria-disabled": !text,
            "aria-label": `${label}: ${text}`,
            title: label,
            onClick: () => copyAddress(text),
            onKeyDown: (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    copyAddress(text);
                }
            },
        };
    };

    // Note: Date/time formatting might need more robust localization depending on requirements
    const formatDate = useCallback((dateString) => {
        const date = new Date(dateString);

        const day = date.getDate();
        const monthIndex = date.getMonth();
        const weekdayIndex = date.getDay();

        const monthKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        const weekdayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

        const monthKey = monthKeys[monthIndex];
        const weekdayKey = weekdayKeys[weekdayIndex];

        const month = t(`common.datetime.short_months.${monthKey}`);
        const weekday = t(`common.datetime.short_weekdays.${weekdayKey}`);

        return `${day} ${month}, ${weekday}`;
    }, [t]);

    const formatTime = (timeString) => {
        const date = new Date(timeString);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const useExpectedUnloadingDate = !!item?.expectedUnloadingDate;
    const unloadingDisplayDate = useExpectedUnloadingDate ? item.expectedUnloadingDate : item?.unloadingDate;

    return (
        <div className={cn(style.shipping_wrapper)}>
            <LineIcon />
            <div className={cn(style.shipping_content, {
                [style.shipping_content__twoColumns]: isMarketplacePage,
            })}>
                <div className={style.shipping_column}>
                    <span className={style.shipping_column_head}>{t('orderInfo.ordersInfoDirections.loadingLabel')}</span>
                    <div className={style.shipping_column_text}>
                        {item?.shippingDate ? formatDate(item.shippingDate) : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                    </div>
                    {item?.shippingDate && item?.shippingDateEnd && (
                      <div className={style.shipping_column_footer}>
                          {t("orderInfo.ordersInfoDirections.timeFrom")} {formatTime(item.shippingDate)} {t("orderInfo.ordersInfoDirections.timeTo")} {formatTime(item.shippingDateEnd)}
                      </div>
                    )}
                </div>
                <div {...getAddressCopyProps(item?.countryFromName, item?.cityFromName, item?.addressFrom)}>
                    <span className={style.shipping_column_head}>{t("orderInfo.ordersInfoDirections.fromLabel")}</span>
                    <div className={style.shipping_column_main}>
                        <img
                          src={getCountryIcon(item?.countryIdFrom)}
                          alt={t("orderInfo.ordersInfoAddressList.countryAlt")}
                          className={style.shipping_column_countryIcon}
                        />
                        <div className={style.shipping_column_text}>
                            {item?.cityFromName}
                        </div>
                    </div>
                    <div className={style.shipping_column_footer}>
                        {item?.addressFrom}
                    </div>
                </div>
                {!isMarketplacePage && (
                    <div className={style.shipping_column}>
                        <span className={style.shipping_column_head}>{t('orderInfo.ordersInfoDirections.senderLabel')}</span>
                        <div className={style.shipping_column_text}>
                            {item?.senderName}
                        </div>
                        <div className={style.shipping_column_footer}>
                            {item?.senderPhoneNumber}
                        </div>
                    </div>
                )}
                <div className={style.shipping_column}>
                    <span className={style.shipping_column_head}>{t('orderInfo.ordersInfoDirections.unloadingLabel')}</span>
                    <div className={style.shipping_column_text}>
                        {unloadingDisplayDate ? formatDate(unloadingDisplayDate) : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                    </div>
                    {useExpectedUnloadingDate ? (
                      <div className={style.shipping_column_footer}>
                          {t('orderInfo.ordersInfoAddressList.estimatedDate')}
                      </div>
                    ) : (item?.unloadingDate && item?.unloadingDateEnd && (
                      <div className={style.shipping_column_footer}>
                          {t("orderInfo.ordersInfoDirections.timeFrom")} {formatTime(item.shippingDate)} {t("orderInfo.ordersInfoDirections.timeTo")} {formatTime(item.unloadingDateEnd)}
                      </div>
                    ))}
                </div>
                <div {...getAddressCopyProps(item?.countryToName, item?.cityToName, item?.addressTo)}>
                    <span className={style.shipping_column_head}>{t('orderInfo.ordersInfoDirections.toLabel')}</span>
                    <span className={style.shipping_column_main}>
                        <img
                            src={getCountryIcon(item?.countryIdTo)}
                            alt={t('orderInfo.ordersInfoAddressList.countryAlt')}
                            className={style.shipping_column_countryIcon}
                        />
                        <div className={style.shipping_column_text}>
                            {item?.cityToName}
                        </div>
                    </span>
                    <span className={style.shipping_column_footer}>
                        {item?.addressTo}
                    </span>
                </div>
                {!isMarketplacePage && (
                    <div className={style.shipping_column}>
                        <span className={style.shipping_column_head}>
                            {t("orderInfo.ordersInfoDirections.recipientLabel")}
                        </span>
                        <span className={style.shipping_column_text}>{item?.recipientName}</span>
                        <span className={style.shipping_column_footer}>{item?.recipientPhoneNumber}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrdersInfoDirections;
